// Broadnet → Monitoreo: transacciones recientes (para conciliar con el Excel del
// proveedor) + regularización de pendientes/reversos + trazabilidad de las llamadas
// del worker al WebService de Broadnet por transacción.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, ChevronRight, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  BnConsultaRow, BnConsultaWsLog, BnTx, BnWsLog, BnWsLogEntry, Campo,
  bnConsultaWsLog, bnConsultasListar, bnDescartar, bnFacturarRecarga, bnRegularizar, bnTransacciones, bnWsLog, fechaLocal, fieldCls, money,
  bnMargen, type BnMargen,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

const hoy = () => fechaLocal();
const ESTADOS_TX = ['aprobada', 'confirmada', 'declinada', 'pendiente', 'reverso_pendiente', 'reversada', 'confirmacion_pendiente', 'fallida'];
const ESTADO_CLS: Record<string, string> = {
  aprobada: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  confirmada: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  declinada: 'bg-muted text-muted-foreground',
  reversada: 'bg-muted text-muted-foreground',
  pendiente: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  reverso_pendiente: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  confirmacion_pendiente: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  fallida: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

// Color del punto/chip de resultado de cada llamada al WebService (verde ok, ámbar
// reintento/timeout, rojo error/declinada/reverso).
const resultadoCls = (resultado: string | null): string => {
  const r = (resultado ?? '').toLowerCase();
  if (/ok|aprobad|confirmar/.test(r)) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (/reintento|timeout/.test(r)) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (/error|declinad|reverso/.test(r)) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  return 'bg-muted text-muted-foreground';
};
const puntoCls = (resultado: string | null): string => {
  const r = (resultado ?? '').toLowerCase();
  if (/ok|aprobad|confirmar/.test(r)) return 'bg-green-500';
  if (/reintento|timeout/.test(r)) return 'bg-amber-500';
  if (/error|declinad|reverso/.test(r)) return 'bg-red-500';
  return 'bg-muted-foreground';
};

export function MonitoreoBroadnet() {
  const [tab, setTab] = useState<'tx' | 'traza'>('tx');
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5" /> Broadnet · Monitoreo</h1>
      <div className="flex items-center gap-2 border-b">
        {([['tx', 'Transacciones'], ['traza', 'Trazabilidad']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'tx' ? <Transacciones /> : <Trazabilidad />}
    </div>
  );
}

function Transacciones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const [estado, setEstado] = useState('');

  const { data: txs = [], isFetching } = useQuery<BnTx[]>({
    queryKey: ['pos-bn-tx', desde, hasta, estado],
    queryFn: () => bnTransacciones(desde || null, hasta || null, null, estado || null),
    staleTime: 0, refetchOnMount: 'always',
  });
  const regularizar = useMutation({
    mutationFn: (id: string) => bnRegularizar(id),
    onSuccess: (r: { estado: string; ya_resuelta?: boolean }) =>
      (r.ya_resuelta ? toast.info(`Ya se había resuelto sola: ${r.estado}`) : toast.success(`Regularizada: ${r.estado}`)),
    onError: (e) => {
      const msg = getErrorMessage(e);
      // La resolvió el worker u otra caja (o un click previo): no es un error del usuario,
      // solo la lista estaba desactualizada. Se refresca en onSettled y se avisa suave.
      if (/no está pendiente/i.test(msg)) toast.info('Esa transacción ya estaba regularizada — actualizo la lista');
      else toast.error(msg);
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['pos-bn-tx'] }); },
  });

  // Salida para la recarga que NUNCA llegó a Broadnet (889): «Regularizar» no
  // sirve ahí, porque da por hecho que hay un worker que la resuelva. Sin
  // worker, esa transacción bloquea la caja para siempre.
  const descartar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => bnDescartar(id, motivo),
    onSuccess: (r) => (r.ya_resuelta
      ? toast.info(`Ya estaba resuelta: ${r.estado}`)
      : toast.success(r.mensaje ?? 'Recarga descartada')),
    onError: (e) => toast.error(getErrorMessage(e)),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['pos-bn-tx'] }); },
  });
  // 901 · Facturar una recarga aprobada que se quedó sin comprobante. Antes
  // sólo se podía desde el modal de la venta, y en cuanto se cerraba ya no había
  // manera: la del 24/09 hubo que emitirla a mano contra la base de producción.
  const facturarRec = useMutation({
    mutationFn: (id: string) => bnFacturarRecarga(id),
    onSuccess: (r) => (r.ya_facturada
      ? toast.info(`Esa recarga ya tenía su factura (${r.codigo})`)
      : toast.success(`Factura ${r.codigo} emitida por ${money(Number(r.total) || 0)}`)),
    onError: (e) => toast.error(getErrorMessage(e), { duration: 9000 }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['pos-bn-tx'] }); },
  });

  const pedirDescarte = async (id: string) => {
    const v = await dlg.pedir('Descartar esta recarga', [
      { key: 'motivo', label: 'Motivo', minimo: 3 },
    ], {
      detalle: 'La da por NO ENVIADA y libera la caja. Hazlo sólo si confirmaste que Broadnet nunca la recibió (el sistema no permitirá descartarla si el proveedor respondió algo).',
      aceptar: 'Descartar',
    });
    if (v?.motivo.trim()) descartar.mutate({ id, motivo: v.motivo.trim() });
  };

  const vendidas = useMemo(() => txs.filter((t) => t.estado === 'aprobada' || t.estado === 'confirmada'), [txs]);
  const pendientes = useMemo(() => txs.filter((t) => ['pendiente', 'reverso_pendiente', 'confirmacion_pendiente'].includes(t.estado)), [txs]);
  const exportFilas = useMemo<Celda[][]>(() => {
    const filas: Celda[][] = txs.map((t, i) => [i + 1, new Date(t.fecha).toLocaleString(LOCALE), t.lote, t.referencia,
      t.caja, t.cajero, t.proveedor, t.celular, Number(t.monto), t.estado, t.autorizacion, t.nota_venta?.trim() ?? null]);
    if (filas.length) filas.push(['', 'Totales (vendidas)', '', '', '', '', '', '', vendidas.reduce((a, t) => a + Number(t.monto), 0), '', '', '']);
    return filas;
  }, [txs, vendidas]);

  return (
    <div className="space-y-4">
      {dlg.ui}
      {pendientes.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-4 py-2 text-sm text-amber-800 dark:text-amber-300">
          {pendientes.length} transacción(es) pendiente(s) de regularizar — mientras haya una pendiente en una caja, esa caja no puede vender otra recarga ni cerrar turno.
        </div>
      )}

      <MargenBroadnet desde={desde} hasta={hasta} />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <Campo label="Desde"><input type="date" className={`${fieldCls} h-9`} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={`${fieldCls} h-9`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label="Estado">
            <select className={`${fieldCls} h-9 w-52`} value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos</option>
              {ESTADOS_TX.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </Campo>
          <div className="ml-auto">
            <ExportarBotones nombre="broadnet_monitoreo" titulo="Broadnet — Monitoreo de transacciones"
              subtitulo={`${desde} a ${hasta} — cruzar por REFERENCIA y AUTORIZACIÓN vs conciliación Broadnet`}
              headers={['N°', 'Fecha', 'Lote', 'Referencia', 'Caja', 'Cajero', 'Proveedor', 'Celular', 'Monto', 'Estado', 'Autorización', 'Nota venta']}
              filas={exportFilas} colsDerecha={[0, 8]} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Transacciones ({txs.length}) · vendido {money(vendidas.reduce((a, t) => a + Number(t.monto), 0))}
          {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Ref.</th>
              <th className="py-2 pr-3">Caja / Cajero</th><th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3">Celular</th>
              <th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th>
              <th className="py-2 pr-3">Autorización</th><th className="py-2 pr-3">Nota venta</th>
              <th className="py-2 pr-3">Factura</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {txs.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin transacciones en el rango</td></tr>}
              {txs.map((t, i) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-xs">{new Date(t.fecha).toLocaleString(LOCALE)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.referencia}</td>
                  <td className="py-2 pr-3 text-xs">{t.caja}<div className="text-muted-foreground">{t.cajero ?? ''}</div></td>
                  <td className="py-2 pr-3">{t.proveedor}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.celular}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(t.monto)}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ESTADO_CLS[t.estado] ?? ''}`} title={t.mensaje ?? ''}>{t.estado.replace('_', ' ')}</span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.autorizacion ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.nota_venta?.trim() || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {t.venta_codigo
                      ? <span className="text-estable">{t.venta_codigo}</span>
                      : ['aprobada', 'confirmada'].includes(t.estado)
                        ? <span className="text-pendiente">sin factura</span>
                        : '—'}
                  </td>
                  <td className="py-2 text-right">
                    {['pendiente', 'reverso_pendiente', 'confirmacion_pendiente'].includes(t.estado) && (
                      <>
                        <Button size="sm" variant="outline" isLoading={regularizar.isPending} onClick={() => regularizar.mutate(t.id)}>Regularizar</Button>
                        {!t.autorizacion && (
                          <Button size="sm" variant="outline" className="ml-1 text-urgente"
                                  isLoading={descartar.isPending} onClick={() => void pedirDescarte(t.id)}>
                            Descartar
                          </Button>
                        )}
                      </>
                    )}
                    {['aprobada', 'confirmada'].includes(t.estado) && !t.venta_codigo && (
                      <Button size="sm" variant="outline" isLoading={facturarRec.isPending}
                              title="Emite la factura de esta recarga (necesita el turno de su caja abierto)"
                              onClick={() => facturarRec.mutate(t.id)}>
                        Facturar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {txs.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={6}>Totales — vendidas {vendidas.length} de {txs.length}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(vendidas.reduce((a, t) => a + Number(t.monto), 0))}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">
            Concilia contra el Excel diario de Broadnet cruzando <span className="font-medium">Referencia + Autorización</span> (exporta esta tabla).
            Recuerda: tienes <span className="font-medium">48 horas</span> para reportar inconsistencias al proveedor.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trazabilidad: línea de tiempo de las llamadas del worker al WebService de
// Broadnet por transacción (bn_ws_log). En modo simulado no hay logs reales.
// ---------------------------------------------------------------------------

function Trazabilidad() {
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  // Tipo de operación: transacciones (recargas/pagos SB) o CONSULTAS DE DEUDA (700)
  const [tipo, setTipo] = useState<'tx' | 'consulta'>('tx');
  const [txId, setTxId] = useState('');
  const [consultaId, setConsultaId] = useState('');

  const { data: txs = [], isFetching: buscandoTx } = useQuery<BnTx[]>({
    queryKey: ['pos-bn-tx', desde, hasta, ''],
    queryFn: () => bnTransacciones(desde || null, hasta || null, null, null),
    enabled: tipo === 'tx',
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: consultas = [], isFetching: buscandoCon } = useQuery<BnConsultaRow[]>({
    queryKey: ['pos-bn-consultas', desde, hasta],
    queryFn: () => bnConsultasListar(desde || null, hasta || null),
    enabled: tipo === 'consulta',
    staleTime: 0, refetchOnMount: 'always',
  });

  const { data: traza, isFetching: cargando, isError } = useQuery<BnWsLog>({
    queryKey: ['pos-bn-ws-log', txId],
    queryFn: () => bnWsLog(txId),
    enabled: tipo === 'tx' && !!txId,
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: trazaCon, isFetching: cargandoCon, isError: errorCon } = useQuery<BnConsultaWsLog>({
    queryKey: ['pos-bn-consulta-ws-log', consultaId],
    queryFn: () => bnConsultaWsLog(consultaId),
    enabled: tipo === 'consulta' && !!consultaId,
    staleTime: 0, refetchOnMount: 'always',
  });

  const buscando = tipo === 'tx' ? buscandoTx : buscandoCon;
  const seleccion = tipo === 'tx' ? txId : consultaId;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <Campo label="Tipo">
            <select className={`${fieldCls} h-9 !w-auto`} value={tipo}
              onChange={(e) => { setTipo(e.target.value as 'tx' | 'consulta'); }}>
              <option value="tx">Transacciones (recargas / pagos)</option>
              <option value="consulta">Consultas de deuda</option>
            </select>
          </Campo>
          <Campo label="Desde"><input type="date" className={`${fieldCls} h-9`} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={`${fieldCls} h-9`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          {tipo === 'tx' ? (
            <Campo label="Transacción" className="min-w-[22rem] flex-1">
              <select className={`${fieldCls} h-9`} value={txId} onChange={(e) => setTxId(e.target.value)}>
                <option value="">— Selecciona una transacción del rango —</option>
                {txs.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.referencia} · {t.proveedor} · {t.celular} · {money(t.monto)} · {t.estado.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </Campo>
          ) : (
            <Campo label="Consulta" className="min-w-[22rem] flex-1">
              <select className={`${fieldCls} h-9`} value={consultaId} onChange={(e) => setConsultaId(e.target.value)}>
                <option value="">— Selecciona una consulta del rango —</option>
                {consultas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {new Date(c.fecha).toLocaleTimeString(LOCALE, { hour12: false })} · {c.proveedor} · {c.referencia_servicio} · {c.estado}
                    {c.deuda != null ? ` · deuda ${money(c.deuda)}` : ''}
                  </option>
                ))}
              </select>
            </Campo>
          )}
          {buscando && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mb-2" />}
        </CardContent>
      </Card>

      {!seleccion && (
        <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
          Elige una {tipo === 'tx' ? 'transacción' : 'consulta de deuda'} del rango para ver su traza de llamadas al WebService de Broadnet.
        </div>
      )}

      {seleccion && (cargando || cargandoCon) && (
        <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto w-6 h-6 animate-spin" /></div>
      )}

      {seleccion && (isError || errorCon) && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
          No se pudo cargar la traza.
        </div>
      )}

      {tipo === 'tx' && txId && !cargando && traza && <TrazaDetalle traza={traza} />}
      {tipo === 'consulta' && consultaId && !cargandoCon && trazaCon?.consulta && <TrazaConsulta traza={trazaCon} />}
    </div>
  );
}

// Traza de una CONSULTA DE DEUDA (script 700): cabecera + misma línea de tiempo.
function TrazaConsulta({ traza }: { traza: BnConsultaWsLog }) {
  const { consulta: c, logs } = traza;
  const puntos: BnWsLogEntry[] = logs.length > 0 ? logs : [
    {
      creado_at: c.creada, fase: 'creada', operacion: null, tipo_transaccion: null, intento: 1,
      http_status: null, duracion_ms: null, codigo_respuesta: null, mensaje: null, autorizacion: null,
      resultado: null, respuesta_xml: null, request_resumen: null, request_xml: null,
    },
    {
      creado_at: c.actualizada, fase: `resuelta → ${c.estado}`, operacion: null, tipo_transaccion: null, intento: c.intentos,
      http_status: null, duracion_ms: null, codigo_respuesta: c.codigo_respuesta, mensaje: c.mensaje, autorizacion: null,
      resultado: c.estado, respuesta_xml: null, request_resumen: null, request_xml: null,
    },
  ];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Consulta de deuda · {c.proveedor}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Dato label="Referencia / cuenta" valor={c.referencia_servicio} mono />
          <Dato label="Referencia Broadnet" valor={c.referencia} mono />
          <div>
            <div className="text-xs text-muted-foreground">Estado final</div>
            <span className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ESTADO_CLS[c.estado] ?? 'bg-muted text-foreground'}`}>
              {c.estado}
            </span>
          </div>
          <Dato label="Intentos" valor={String(c.intentos)} />
          <Dato label="Deuda" valor={c.deuda != null ? money(c.deuda) : '—'} mono />
          <Dato label="Comisión" valor={c.comision != null ? money(c.comision) : '—'} mono />
          <Dato label="Titular" valor={c.titular ?? '—'} />
          <Dato label="Creada" valor={new Date(c.creada).toLocaleString(LOCALE)} />
        </CardContent>
      </Card>
      <LineaTiempo puntos={puntos} sinLogs={logs.length === 0} />
      {traza.pago && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" />
            Pago asociado · ref {traza.pago.referencia} · {money(Number(traza.pago.monto))} · {traza.pago.estado.replace('_', ' ')}
            {traza.pago.autorizacion ? ` · aut. ${traza.pago.autorizacion}` : ''}
          </CardTitle></CardHeader>
          <CardContent>
            {traza.pago.logs.length
              ? <PuntosTimeline puntos={traza.pago.logs} />
              : <p className="text-xs text-muted-foreground">El pago aún no tiene llamadas registradas (o es anterior a la bitácora).</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TrazaDetalle({ traza }: { traza: BnWsLog }) {
  const { tx, logs } = traza;
  // En modo simulado no hay llamadas reales al WS: sintetizamos 2 puntos desde la tx.
  const puntos: BnWsLogEntry[] = logs.length > 0 ? logs : [
    {
      creado_at: tx.creada, fase: 'creada', operacion: null, tipo_transaccion: null, intento: 1,
      http_status: null, duracion_ms: null, codigo_respuesta: null, mensaje: null, autorizacion: null,
      resultado: null, respuesta_xml: null, request_resumen: null, request_xml: null,
    },
    {
      creado_at: tx.actualizada, fase: `resuelta → ${tx.estado.replace('_', ' ')}`, operacion: null, tipo_transaccion: null, intento: tx.intentos,
      http_status: null, duracion_ms: null, codigo_respuesta: tx.codigo_respuesta, mensaje: tx.mensaje, autorizacion: tx.autorizacion,
      resultado: tx.estado, respuesta_xml: null, request_resumen: null, request_xml: null,
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Transacción {tx.referencia}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Dato label="Proveedor" valor={tx.proveedor} />
          <Dato label="Celular" valor={tx.celular} mono />
          <Dato label="Monto" valor={money(tx.monto)} mono />
          <div>
            <div className="text-xs text-muted-foreground">Estado final</div>
            <span className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ESTADO_CLS[tx.estado] ?? ''}`}>
              {tx.estado.replace('_', ' ')}
            </span>
          </div>
          <Dato label="Autorización" valor={tx.autorizacion ?? '—'} mono />
          <Dato label="Intentos" valor={String(tx.intentos)} />
          <Dato label="Creada" valor={new Date(tx.creada).toLocaleString(LOCALE)} />
          <Dato label="Actualizada" valor={new Date(tx.actualizada).toLocaleString(LOCALE)} />
        </CardContent>
      </Card>

      <LineaTiempo puntos={puntos} sinLogs={logs.length === 0} />
      {traza.consulta && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" />
            Consulta de deuda previa · {new Date(traza.consulta.creada).toLocaleTimeString(LOCALE, { hour12: false })} · {traza.consulta.estado}
            {traza.consulta.deuda != null ? ` · deuda ${money(Number(traza.consulta.deuda))} + ${money(Number(traza.consulta.comision ?? 0))}` : ''}
            {traza.consulta.titular ? ` · ${traza.consulta.titular}` : ''}
          </CardTitle></CardHeader>
          <CardContent>
            {traza.consulta.logs.length
              ? <PuntosTimeline puntos={traza.consulta.logs} />
              : <p className="text-xs text-muted-foreground">La consulta no tiene llamadas registradas (anterior a la bitácora).</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Línea de tiempo de llamadas al WS — compartida por transacciones y consultas.
function LineaTiempo({ puntos, sinLogs }: { puntos: BnWsLogEntry[]; sinLogs: boolean }) {
  return (
    <>
      {sinLogs && (
        <p className="text-xs text-muted-foreground px-1">
          En modo simulado no hay llamadas reales al WebService; la traza detallada aparece en producción (worker).
        </p>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Línea de tiempo</CardTitle></CardHeader>
        <CardContent><PuntosTimeline puntos={puntos} /></CardContent>
      </Card>
    </>
  );
}

// La lista de puntos sola (sin Card): la reutilizan la traza principal y las
// secciones asociadas consulta↔pago (702).
function PuntosTimeline({ puntos }: { puntos: BnWsLogEntry[] }) {
  return (
          <ol className="relative border-l border-border ml-2 space-y-5">
            {puntos.map((l, i) => (
              <li key={i} className="ml-4">
                <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ${puntoCls(l.resultado)}`} />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(l.creado_at).toLocaleString(LOCALE)}
                  </span>
                  <span className="flex items-center gap-1 text-sm font-medium capitalize">
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    {l.fase}{l.operacion ? ` · ${l.operacion}` : ''}{l.tipo_transaccion ? ` · ${l.tipo_transaccion}` : ''}
                  </span>
                  {l.resultado && (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${resultadoCls(l.resultado)}`}>
                      {l.resultado.replace('_', ' ')}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">intento {l.intento}</span>
                  {l.http_status != null && <span className="text-xs text-muted-foreground">HTTP {l.http_status}</span>}
                  {l.duracion_ms != null && <span className="text-xs text-muted-foreground">{l.duracion_ms} ms</span>}
                </div>
                {(l.codigo_respuesta || l.mensaje) && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {l.codigo_respuesta ? <span className="font-mono">{l.codigo_respuesta}</span> : null}
                    {l.codigo_respuesta && l.mensaje ? ' · ' : ''}
                    {l.mensaje ?? ''}
                  </p>
                )}
                {l.autorizacion && <p className="mt-0.5 text-xs text-muted-foreground">Autorización: <span className="font-mono">{l.autorizacion}</span></p>}
                {l.request_resumen && <p className="mt-0.5 text-xs text-muted-foreground">Solicitud: {l.request_resumen}</p>}
                {l.request_xml && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-primary">Ver XML enviado (request)</summary>
                    <pre className="mt-1 max-h-64 overflow-x-auto rounded-md border bg-muted/30 p-2 text-[11px] leading-snug">{l.request_xml}</pre>
                  </details>
                )}
                {l.respuesta_xml && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-primary">Ver XML de respuesta</summary>
                    <pre className="mt-1 max-h-64 overflow-x-auto rounded-md border bg-muted/30 p-2 text-[11px] leading-snug">{l.respuesta_xml}</pre>
                  </details>
                )}
              </li>
            ))}
          </ol>
  );
}

function Dato({ label, valor, mono = false }: { label: string; valor: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono text-sm' : 'text-sm'}>{valor}</div>
    </div>
  );
}

// Lo que de verdad queda de cada recarga. Va aquí y no en un reporte aparte
// porque el error que importa —facturar con IVA algo que se compra casi a la
// par, y perder dinero en cada venta— sólo se ve poniendo los dos números
// juntos, y hay que verlo el primer día.
function MargenBroadnet({ desde, hasta }: { desde: string; hasta: string }) {
  const { data } = useQuery<BnMargen>({
    queryKey: ['bn-margen', desde, hasta],
    queryFn: () => bnMargen(desde || null, hasta || null),
  });
  if (!data || data.transacciones === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Lo que deja Broadnet</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-muted-foreground">Vendido</div><div className="text-lg font-semibold font-mono tabular-nums">{money(data.vendido)}</div></div>
          <div><div className="text-xs text-muted-foreground">Costo</div><div className="text-lg font-semibold font-mono tabular-nums">{money(data.costo)}</div></div>
          <div><div className="text-xs text-muted-foreground">Queda</div>
            <div className={`text-lg font-semibold font-mono tabular-nums ${Number(data.margen) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(data.margen)}</div></div>
          <div><div className="text-xs text-muted-foreground">Transacciones</div><div className="text-lg font-semibold tabular-nums">{data.transacciones}</div></div>
        </div>
        {data.con_perdida > 0 && (
          <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-300">
            {data.con_perdida} producto(s) están dejando <b>pérdida</b> en cada venta. Revisa la comisión en
            Productos y el IVA en Conexión: facturar con IVA el valor completo de algo que se compra casi a la
            par no deja margen.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 pr-3">Producto</th><th className="py-1 pr-3">Factura</th>
              <th className="py-1 pr-3 text-right">N°</th><th className="py-1 pr-3 text-right">Vendido</th>
              <th className="py-1 pr-3 text-right">Costo</th><th className="py-1 pr-3 text-right">Queda</th>
              <th className="py-1 pr-3 text-right">%</th>
            </tr></thead>
            <tbody>
              {data.detalle.map((f, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1 pr-3">{f.proveedor_nombre}<div className="text-xs text-muted-foreground">{f.servicio_nombre}</div></td>
                  <td className="py-1 pr-3 text-xs">{f.facturacion === 'comercio' ? 'El comercio' : 'Broadnet'}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{f.n}</td>
                  <td className="py-1 pr-3 text-right font-mono tabular-nums">{money(f.vendido)}</td>
                  <td className="py-1 pr-3 text-right font-mono tabular-nums">{money(f.costo)}</td>
                  <td className={`py-1 pr-3 text-right font-mono tabular-nums font-semibold ${Number(f.margen) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(f.margen)}</td>
                  <td className={`py-1 pr-3 text-right font-mono tabular-nums ${Number(f.margen) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{Number(f.margen_pct).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
